import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {App, AppId} from '../../../../../shared/models/app';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {AppsService} from '../../../../../core/services/apps.service';
import {makeTileIdMap, Tile, TILE_TYPE_SUPER_FOLDER, TileId, TileIdMap} from '../../../../../shared/models/tile';
import {TilesService} from '../../../../../core/services/tiles.service';
import {TileGroup} from '../../../../../shared/models/tile-group';
import {filter, map, mergeMap, take, toArray} from 'rxjs/operators';
import {Option, OptionGroupMultiSelect} from '../../../../../shared/models/option';
import {concat, forkJoin, from, Observable, tap} from 'rxjs';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {
  SaveRevertButtonsComponent,
  SaveRevertButtonsStatus,
} from '../../../../components/save-revert-buttons/save-revert-buttons.component';
import {SessionService} from '../../../../../core/services/session.service';
import {TileOrderSorterComponent} from '../../../../components/tile-order-sorter/tile-order-sorter.component';
import {DevicesService} from '../../../../../core/services/devices.service';
import {HubsService} from '../../../../../core/services/hubs.service';
import {SuperFolderStructure} from '../../../../../shared/models/structure';
import {TileWithChildren} from '../../../../../shared/models/tile-with-children';
import {SpinnerComponent} from '../../../../components/spinner/spinner.component';
import {FormsModule} from '@angular/forms';
import {AppBulkEditorComponent} from '../../../../components/app-bulk-editor/app-bulk-editor.component';
import { PermissionsService } from 'src/core/services/permissions.service';
import { Permission } from 'src/shared/models/fga/permission';
import { ObjectType } from 'src/shared/models/object.model';

@Component({
  selector: 'motn-tile-groups-mosaic',
  templateUrl: './tile-groups-mosaic.component.html',
  styleUrls: ['./tile-groups-mosaic.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkDropList,
    SpinnerComponent,
    CdkDrag,
    CdkDragHandle,
    FormsModule,
    TileOrderSorterComponent,
    SaveRevertButtonsComponent,
    AppBulkEditorComponent,
  ],
})
export class TileGroupsMosaicComponent implements OnInit, OnDestroy {
  app: App;
  appId: AppId;
  appLinkedTileIds: TileId[] = [];
  tileGroups: TileGroup[];
  bulkEditIsOpen = false;
  bulkEditIsLoading = false;
  editableTiles: TileWithChildren[] = [];

  private originalTileGroups: TileGroup[];

  selectableTiles: TileIdMap;
  selectableTilesOptionGroups: { [groupIndex: number]: OptionGroupMultiSelect } = {};

  status: SaveRevertButtonsStatus = '';

  @ViewChild('tileOrderSorterComponent') private tileOrderSorter: TileOrderSorterComponent;


  constructor(
    private store: Store<AppState>,
    private appsService: AppsService,
    private tilesService: TilesService,
    private devicesService: DevicesService,
    private hubsService: HubsService,
    private permissionsService: PermissionsService,
    private shadeTools: ShadeToolsService,
    private session: SessionService,
  ) {
  }


  ngOnInit(): void {
    this.appId = this.appsService.currentAppId;

    const apps$: Observable<App> = this.store.select('apps').pipe(
      map((state) => {
        return state.apps.find((a) => {
          return a.ID === this.appId;
        });
      }),
      filter(a => !!a),
      take(1),
    );

    forkJoin([
      apps$,
      this.tilesService.fetchAppTiles$(this.appId),
      this.tilesService.fetchUserTiles$(),
    ]).subscribe({
      next: ([app, appTiles, userTiles]) => {
        this.app = App.create(app);
        this.appLinkedTileIds = appTiles.map(tile => tile.ID);
        this.selectableTiles = makeTileIdMap(userTiles);
        this.tileGroups = this.app.TileGroups;

        this.storeOriginalTileGroups();

        this.prepareTileGroups();
        this.prepareSelectableTiles();
      },
      error: (error) => {
        console.error('Failed to initialize tile groups:', error);
        this.shadeTools.errorDialog('Failed to load tile groups. Please refresh the page and try again.');
      }
    });

    this.appsService.fetchApp(this.appId);
  }


  ngOnDestroy() {
  }


  private storeOriginalTileGroups() {
    this.originalTileGroups = JSON.parse(JSON.stringify(this.tileGroups));
  }


  getTileListId(groupIndex: number): string {
    return `tile-list-${groupIndex}`;
  }


  getConnectedTileListIds(excludeIndex: number): string[] {
    const ids: string[] = [];

    for (let i = 0; i < this.tileGroups.length; i++) {
      if (i !== excludeIndex) {
        ids.push(`tile-list-${i}`);
      }
    }

    return ids;
  }


  updateStatus() {
    this.status = 'edited';
  }


  /**
   * Handles a dragged-and-dropped tile group. Will refuse to allow
   * the group whose Ungrouped property is true to be moved from
   * index 0.
   *
   * @param $event
   */
  dropTileGroup($event: CdkDragDrop<TileGroup[]>) {
    if ($event.currentIndex !== 0) {
      moveItemInArray(
        $event.container.data,
        $event.previousIndex,
        $event.currentIndex,
      );

      this.updateStatus();
    } else {
      this.shadeTools.errorDialog(`The top tile group placement is reserved for tiles that haven't been placed in any other group.\n\nYou can drag any tile group to any placement except the very top one.`);
    }
  }


  dropTile($event: CdkDragDrop<TileId[]>) {
    this.updateStatus();
    this.prepareSelectableTiles();
  }

  sortGroupById(groupIndex: number, sortedTileIds: TileId[]) {
    this.tileGroups[groupIndex].Tiles = sortedTileIds;
    this.updateStatus();
  }

  /**
   * Run this once and before running prepareSelectableTiles.
   *
   * This function automatically removes tile IDs from tile groups
   * for tiles that have been deleted (because tile groups aren't
   * automatically updated when a tile is deleted).
   *
   * @see prepareSelectableTiles
   * @private
   */
  private prepareTileGroups() {
    if (this.tileGroups.length === 0) {
      this.tileGroups.push({
        Name: '',
        Tiles: [],
        Ungrouped: true,
      });
    }

    // Use this to find tiles that are new and have not been added
    // to the tileGroups array yet so we can add them (otherwise
    // they'll never appear in the sorter). Since this.appLinkedTileIds
    // is a definitive list of all tiles linked to this app, we
    // will determine later which tile IDs showed up in tileGroups
    // but not this.appLinkedTileIds and apply a fix.
    const tileIdsInGroups: TileId[] = [];

    for (let _i = 0; _i < this.tileGroups.length; _i++) {
      const index = _i;
      const tileGroup = this.tileGroups[index];

      this.tileGroups[index].Tiles = tileGroup.Tiles.filter(
        (tileId) => {
          tileIdsInGroups.push(tileId); // yes, this is unrelated to the filter operation, but it was convenient to stick this here
          return this.selectableTiles[tileId];
        },
      );
    }

    // Any linked tiles that were never saved to tileGroups[0]
    // get added to tileGroups[0] here
    for (let tileId of this.appLinkedTileIds) {
      if (tileIdsInGroups.includes(tileId)) {
        continue;
      }

      if (this.selectableTiles.hasOwnProperty(tileId)) {
        this.tileGroups[0].Tiles.push(tileId);
      }
    }
  }


  /**
   * Call this anytime the contents of a tile group have changed
   * and during initialization of this component.
   *
   * @private
   */
  private prepareSelectableTiles(): void {
    // For each tile group, create selectable options for all available tiles
    this.tileGroups.forEach((tileGroup, groupIndex) => {
      // Map each tile to an option object
      const options: Option[] = Object.values(this.selectableTiles).map(tile => {
        const facets = tile.facets;
        const option: Option = {
          id: String(tile.ID),
          name: tile.Name,
          subtitle: facets.length ? facets.map(f => f.Value).join(' · ') : undefined,
          selected: tileGroup.Tiles.includes(tile.ID),
          pill: {
            letter: 't',
            text: String(tile.ID)
          },
          callback: () => this.handleTileSelection(groupIndex, tile.ID, option)
        };

        return option;
      });

      // Store the options group for this tile group
      this.selectableTilesOptionGroups[groupIndex] = new OptionGroupMultiSelect(options);
    });
  }

  private handleTileSelection(groupIndex: number, tileId: TileId, option: Option): void {
    const groupTiles = this.tileGroups[groupIndex].Tiles;
    const isCurrentlySelected = groupTiles.indexOf(tileId) > -1;

    // Handle removing or adding the tile
    const action$ = isCurrentlySelected
      ? this.removeTile(groupIndex, tileId)
      : this.addTile(groupIndex, tileId);

    // If not permitted, revert the selection state
    action$.subscribe(permitted => {
      if (!permitted) {
        window.setTimeout(() => {
          option.selected = isCurrentlySelected;
          this.tileOrderSorter.closeTilesMenu();
        }, 0);
      }
    });
  }


  /**
   * This is meant to help us determine if a tile can be added to or
   * removed from a group in situations where the user doesn't have a
   * grant for the tile but the tile is/isn't already present in one/none
   * of the groups and thus the user wouldn't need a grant to add or
   * remove the tile to/from a different group.
   *
   * @param tileId
   */
  tileAppearsInHowManyGroups(tileId: TileId): number {
    let count = 0;

    for (let group of this.tileGroups) {
      for (let tile of group.Tiles) {
        if (tile === tileId) {
          count++;
        }
      }
    }

    return count;
  }

  private userCanConfigureTile(tileId: TileId): Observable<boolean> {
    return this.permissionsService.hasPermissions({
      UserID: this.session.userId,
      Permissions: [Permission.CAN_CONFIGURE],
      ObjectID: tileId,
      ObjectType: ObjectType.TILE,
    });
  }

  private userCanConfigureApp(): Observable<boolean> {
    return this.permissionsService.hasPermissions({
      UserID: this.session.userId,
      Permissions: [Permission.CAN_CONFIGURE],
      ObjectID: this.appId,
      ObjectType: ObjectType.APP,
    });
  }


  /**
   *
   * @param groupIndex
   * @param tileId
   * @return {boolean} If false, authorization error
   */
  addTile(groupIndex: number, tileId: TileId): Observable<boolean> {
    const permissionChecks$ = forkJoin({
      canConfigureTile: this.userCanConfigureTile(tileId),
      canConfigureApp: this.userCanConfigureApp()
    });

    return permissionChecks$.pipe(
      map(({ canConfigureTile, canConfigureApp }) => {
        // Validate permissions if tile is not already in any groups
        const isNewTile = this.tileAppearsInHowManyGroups(tileId) === 0;
        const hasRequiredPermissions = canConfigureTile && canConfigureApp;

        if (!hasRequiredPermissions && isNewTile) {
          this.showPermissionsError();
          return false;
        }

        // Add tile to group if not already present
        const groupTiles = this.tileGroups[groupIndex].Tiles;
        if (!groupTiles.includes(tileId)) {
          groupTiles.push(tileId);
          this.prepareSelectableTiles();
          this.updateStatus();
        }

        return true;
      })
    );
  }

  private showPermissionsError(): void {
    this.shadeTools.errorDialog(
      `To add a tile to an app, BOTH of the following must be true:
      1. You own or can manage (via sharing) the tile
      2. You own or can manage (via sharing) the app`
    );
  }


  /**
   *
   * @param groupIndex
   * @param tileId
   * @return {boolean} If false, authorization error
   */
  removeTile(groupIndex: number, tileId: TileId): Observable<boolean> {
    // Check if user has required permissions to remove the tile
    const permissionChecks$ = forkJoin({
      canConfigureTile: this.userCanConfigureTile(tileId),
      canConfigureApp: this.userCanConfigureApp()
    });

    return permissionChecks$.pipe(
      map(({ canConfigureTile, canConfigureApp }) => {
        // User needs either tile or app management permissions to remove the last instance
        const userHasPermissions = canConfigureTile || canConfigureApp;
        const isRemovingLastInstance = this.tileAppearsInHowManyGroups(tileId) < 2;

        // Show error if user lacks permissions and this is the last instance
        if (!userHasPermissions && isRemovingLastInstance) {
          this.showRemovalPermissionsError();
          return false;
        }

        this.removeTileFromGroup(groupIndex, tileId);

        // Update UI state
        this.prepareSelectableTiles();
        this.updateStatus();

        return true;
      })
    );
  }

  private showRemovalPermissionsError(): void {
    this.shadeTools.errorDialog(
      `To remove a tile entirely from an app, either of the following must be true:
      1. You own or can manage (via sharing) the tile
      2. You own or can manage (via sharing) the app`
    );
  }

  private removeTileFromGroup(groupIndex: number, tileId: TileId): void {
    const groupTiles = this.tileGroups[groupIndex].Tiles;
    const tileIndex = groupTiles.indexOf(tileId);

    if (tileIndex > -1) {
      groupTiles.splice(tileIndex, 1);
    }
  }


  showUngroupedInfo() {
    this.shadeTools.infoDialog(`The "Ungrouped Tiles" group is where tiles go first after being linked to an app.\n\nYou can move tiles in and out of this special group, but you cannot delete it or move it like you can other tile groups.`);
  }


  addGroup() {
    this.tileGroups.push(
      {
        Name: 'New Group',
        Tiles: [],
        Ungrouped: false,
      },
    );

    this.prepareSelectableTiles();

    setTimeout(() => {
      window.scrollTo({
        behavior: 'smooth',
        top: document.body.scrollHeight,
      });
    }, 0);
  }


  getConfigurableTiles$(tilesIds: TileId[]): Observable<Tile[]> {
    return from(tilesIds).pipe(
      mergeMap(tileId => this.tilesService.canConfigureTile$(tileId).pipe(
        filter(canConfigure => canConfigure),
        map(() => this.tilesService.sessUserTiles[tileId])
      )),
      toArray(),
    );
  }


  getAppWithFolderTilesIds(): TileId[] {
    let allTiles = [];
    this.appLinkedTileIds.forEach(tileId => {
      const tile = this.tilesService.sessUserTiles[tileId];
      if (tile && tile.Type === TILE_TYPE_SUPER_FOLDER) {
        allTiles = [...allTiles, ...(tile.Structure as SuperFolderStructure).TileIDs];
      }
    });
    allTiles = [...this.appLinkedTileIds, ...allTiles];
    return allTiles;
  }


  openBulkEdit() {
    this.bulkEditIsLoading = true;
    const tileIds = this.getAppWithFolderTilesIds();
    if (tileIds && tileIds.length > 0) {
      this.getConfigurableTiles$(tileIds).subscribe({
        next: (configurableTiles: Tile[]) => {
          configurableTiles.sort((tileA, tileB) => tileA.ID.localeCompare(tileB.ID));
          let editableTiles: TileWithChildren[] = [];
          this.appLinkedTileIds.forEach(tileId => {
            const configurableTile = configurableTiles.find(tile => tile.ID === tileId);
            if (!!configurableTile) {
              let configurableTileWithChildren = TileWithChildren.create(configurableTile);
              if (configurableTileWithChildren.Type === TILE_TYPE_SUPER_FOLDER) {
                const childTileIds = (configurableTileWithChildren.Structure as SuperFolderStructure).TileIDs;
                const childTiles = [];
                childTileIds.forEach( childTileId => {
                  const childTile = configurableTiles.find(mT => mT.ID === childTileId)
                  if (!!childTile) {
                    childTiles.push(Tile.create(childTile));
                  }
                })
                configurableTileWithChildren.children = childTiles;
              }
              editableTiles = [...editableTiles,  configurableTileWithChildren];
            }
          })
          this.editableTiles = editableTiles;
          this.bulkEditIsOpen = true;
          this.bulkEditIsLoading = false;
        },
        error: (error) => {
          console.error('Failed to load configurable tiles:', error);
          this.bulkEditIsLoading = false;
          this.shadeTools.errorDialog('Failed to load editable tiles. Please try again.');
        }
      });
    } else {
      // we open empty bulk edit to show the message that there're no editable tiles
      this.bulkEditIsOpen = true;
      this.bulkEditIsLoading = false;
    }
  }


  deleteGroup(groupIndex: number) {
    const orphanedTileIds: TileId[] = [...this.tileGroups[groupIndex].Tiles];

    if (orphanedTileIds.length) {
      this.shadeTools.simpleOkDialog('Tiles Moved', 'The tile group was deleted and its tiles were moved to the "Ungrouped Tiles" group.');

      for (let tileId of orphanedTileIds) {
        if (!this.tileGroups[0].Tiles.includes(tileId)) {
          this.tileGroups[0].Tiles.push(tileId);
        }
      }
    }

    this.tileGroups.splice(groupIndex, 1);

    this.updateStatus();
  }


  revert() {
    this.app.TileGroups = this.tileGroups = JSON.parse(JSON.stringify(this.originalTileGroups));
    this.prepareTileGroups();
    this.prepareSelectableTiles();
  }


  save() {
    const tileIdsInGroups: TileId[] = [];

    {
      // This is just to get unique keys; gets converted to array later
      const tilesInGroups: TileIdMap = {};

      for (let tileGroup of this.tileGroups) {
        for (let tileId of tileGroup.Tiles) {
          tilesInGroups[tileId] = this.selectableTiles[tileId];
        }
      }

      Object.keys(tilesInGroups).map(
        (tileId) => {
          tileIdsInGroups.push(tileId);
        },
      );
    }

    // We'll add all observables we need to run to this array
    const obs$: Observable<any>[] = [];

    // Link newly added tiles
    for (let tileId of tileIdsInGroups) {
      if (!this.appLinkedTileIds.includes(tileId)) {
        obs$.push(this.tilesService.linkWithApp$(tileId, [this.appId]).pipe(tap(_ => {
          this.appLinkedTileIds.push(tileId);
        })));
      }
    }

    // Unlink newly removed tiles and remove them from this.appLinkedTileIds
    for (let i = this.appLinkedTileIds.length - 1; i >= 0; i--) {
      const tileId = this.appLinkedTileIds[i];
      if (!tileIdsInGroups.includes(tileId)) {
        obs$.push(this.tilesService.unlinkFromApp$(tileId, [this.appId]));
        this.appLinkedTileIds.splice(i, 1);
      }
    }

    obs$.push(this.appsService.saveAppTileGroups$(this.appId, this.tileGroups));

    concat(...obs$).subscribe({
      error: (err) => {
        this.status = 'edited';

        if (err?.status === 403) {
          this.shadeTools.errorDialog('An unexpected permissions error was encountered. Refresh the app and try again.');
        } else {
          this.shadeTools.errorDialog();
        }
      },
      complete: () => {
        this.status = 'succeeded';
        this.storeOriginalTileGroups();
      },
    });
  }


  onBulkEditorClose(): void {
    this.bulkEditIsOpen = false;
    this.ngOnInit(); // to refresh the tiles
  }
}
